import { ARTICLE_CTA_RENDITIONS, ArticleCtaSlot } from '@olagg/db-types'

/**
 * Los cinco slots de R7, con el tipo (primario o persistente) a la vista: es lo
 * que explica que `sticky_mobile` pueda convivir con un primario.
 */
export const slotOptions: { value: ArticleCtaSlot, label: string }[] = [
  { value: 'right_rail', label: 'Columna derecha (primario, ≥ lg)' },
  { value: 'in_body_mid', label: 'Medio del cuerpo (primario)' },
  { value: 'end_of_article', label: 'Fin del artículo (primario)' },
  { value: 'in_body_top', label: 'Después del primer párrafo (reservado: NO se muestra todavía)' },
  { value: 'sticky_mobile', label: 'Barra inferior móvil (persistente, < lg)' },
]

export const renderTypeOptions = [
  { value: 'standard', label: 'Estándar (título, arte y botón)' },
  { value: 'poll', label: 'Encuesta (opciones seleccionables)' },
]

export const langOptions = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'Inglés' },
  { value: 'pt', label: 'Portugués' },
]

export const deviceOptions = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'desktop', label: 'Desktop' },
]

export const loggedInOptions = [
  { value: 'any', label: 'Indistinto' },
  { value: 'true', label: 'Solo logueados' },
  { value: 'false', label: 'Solo anónimos' },
]

export const CONTENT_LANGS = ['es', 'en', 'pt'] as const

export const langLabels: Record<string, string> = {
  es: 'Español',
  en: 'Inglés',
  pt: 'Portugués',
}

export const renditionFor = (slot?: string) =>
  ARTICLE_CTA_RENDITIONS[(slot || 'in_body_mid') as ArticleCtaSlot] ?? ARTICLE_CTA_RENDITIONS.in_body_mid

/** Resumen del targeting para la tabla: sin esto no se entiende una fila. */
export const targetingSummary = (targeting?: {
  tags?: string[] | null,
  lang?: string[] | null,
  device?: string[] | null,
  loggedIn?: boolean | null,
}) => {
  if (!targeting) return 'Sin filtros'

  const parts: string[] = []

  if (targeting.tags?.length) parts.push(`tags: ${targeting.tags.join(', ')}`)
  if (targeting.lang?.length) parts.push(`idioma: ${targeting.lang.join('/')}`)
  if (targeting.device?.length) parts.push(`device: ${targeting.device.join('/')}`)
  if (targeting.loggedIn === true) parts.push('logueados')
  if (targeting.loggedIn === false) parts.push('anónimos')

  return parts.length ? parts.join(' · ') : 'Sin filtros'
}

/**
 * Precedencia de los slots primarios por device, igual que en la app (R7). Se
 * recorre en orden y gana el primero con candidato: en la página se ve UN solo
 * CTA primario a la vez.
 */
const PRIMARY_PRECEDENCE: Record<'desktop' | 'mobile', ArticleCtaSlot[]> = {
  desktop: ['right_rail', 'in_body_mid', 'end_of_article'],
  mobile: ['in_body_mid', 'end_of_article'],
}

/** `in_body_top` está reservado: todavía no se monta en la página. */
const NOT_MOUNTED: ArticleCtaSlot[] = ['in_body_top']

const shortLabel: Record<ArticleCtaSlot, string> = {
  right_rail: 'columna derecha',
  in_body_mid: 'medio del cuerpo',
  end_of_article: 'fin del artículo',
  in_body_top: 'después del primer párrafo',
  sticky_mobile: 'barra inferior móvil',
}

const winnerFor = (device: 'desktop' | 'mobile', activeSlots: ArticleCtaSlot[]) =>
  PRIMARY_PRECEDENCE[device].find(slot => activeSlots.includes(slot))

/**
 * Por qué un CTA activo puede no verse nunca. Es la pregunta que el formulario no
 * contesta: se carga una variante en el medio del cuerpo, queda activa, y no
 * aparece porque hay otra en la columna derecha, que tiene precedencia en
 * escritorio. Sin esto, la regla sólo se descubre probando.
 */
export const visibilityNote = (slot: ArticleCtaSlot, activeSlots: ArticleCtaSlot[]) => {
  if (NOT_MOUNTED.includes(slot)) return 'Reservado: todavía no se muestra en la página'
  if (slot === 'sticky_mobile') return 'Sólo en móvil · convive con el primario'

  const onDesktop = winnerFor('desktop', activeSlots) === slot
  const onMobile = winnerFor('mobile', activeSlots) === slot

  if (onDesktop && onMobile) return 'Se muestra en escritorio y en móvil'

  if (onDesktop) {
    const mobileWinner = winnerFor('mobile', activeSlots)

    return mobileWinner
      ? `Sólo en escritorio · en móvil gana ${shortLabel[mobileWinner]}`
      : 'Sólo en escritorio'
  }

  if (onMobile) {
    const desktopWinner = winnerFor('desktop', activeSlots)

    return desktopWinner
      ? `Sólo en móvil · en escritorio gana ${shortLabel[desktopWinner]}`
      : 'Sólo en móvil'
  }

  const desktopWinner = winnerFor('desktop', activeSlots)
  const mobileWinner = winnerFor('mobile', activeSlots)
  const reasons = [
    desktopWinner ? `en escritorio gana ${shortLabel[desktopWinner]}` : null,
    mobileWinner ? `en móvil gana ${shortLabel[mobileWinner]}` : null,
  ].filter(Boolean)

  return `No se muestra · ${reasons.join(' y ')}`
}

/**
 * Los placeholders que el servidor sabe resolver. El backend rechaza cualquier
 * otro al guardar, así que esta lista es informativa: está acá para que no haya
 * que adivinarlos ni buscarlos en el código.
 */
export const PLACEHOLDER_HELP = [
  '{prize} y {prize_image}: el premio de La Garra de esta semana y su arte',
  '{quantity_left}: cuántas unidades quedan de ese premio',
  '{product}, {price} y {product_image}: el SKU de la tienda del juego del artículo',
  '{product_slug}: para el destino, ej. /tienda/{product_slug}',
].join(' · ')
